import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Form, Modal, Tab, Table, Tabs } from 'react-bootstrap';
import Pagination from 'react-js-pagination';
import { toast } from 'react-toastify';
import { Get, Put } from '../../utils/httpProvider';
import { ACCOUNTAUTHINFO_API, WORK_RESULT_API } from '../../utils/apiUrl';
import { objectShowNumber } from '../../utils/define';

const fmt = value => value === null || value === undefined ? '-' : Number(value).toFixed(2);
const fmtDate = value => value ? new Date(value).toLocaleString('vi-VN') : '-';
const normalize = value => (value || '').toString().trim().toLocaleLowerCase('vi-VN');

const toInputDate = value => {
    const date = new Date(value);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
};

const getDefaultToDate = () => toInputDate(new Date());
const getDefaultFromDate = () => {
    const today = new Date();
    const lastDayOfPreviousMonth = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
    const date = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        Math.min(today.getDate(), lastDayOfPreviousMonth)
    );
    return toInputDate(date);
};

const getPagedData = response => {
    const data = response?.data ?? response?.Data ?? [];
    return Array.isArray(data) ? data : (data?.$values || []);
};

const getArrayData = response => {
    const data = response?.data ?? response?.Data ?? response ?? [];
    return Array.isArray(data) ? data : (data?.$values || []);
};

const getFullName = accountInfo => {
    const fullName = [accountInfo?.lastName, accountInfo?.middleName, accountInfo?.firstName]
        .filter(Boolean).join(' ').trim();
    return fullName || accountInfo?.account?.username || '';
};

const getErrorMessage = error => {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    const validationErrors = error?.errors;
    if (validationErrors) {
        const firstError = Object.values(validationErrors).flat()[0];
        if (firstError) return firstError;
    }
    return 'Không thể lưu đánh giá.';
};

const WorkResultList = () => {
    const [rows, setRows] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [keywordInput, setKeywordInput] = useState('');
    const [keyword, setKeyword] = useState('');
    const [workType, setWorkType] = useState('');
    const [status, setStatus] = useState('');
    const [evaluated, setEvaluated] = useState('false');
    const [fromDateInput, setFromDateInput] = useState(getDefaultFromDate);
    const [toDateInput, setToDateInput] = useState(getDefaultToDate);
    const [fromDate, setFromDate] = useState(getDefaultFromDate);
    const [toDate, setToDate] = useState(getDefaultToDate);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selected, setSelected] = useState(null);
    const [percent, setPercent] = useState('');
    const [historyItem, setHistoryItem] = useState(null);
    const [history, setHistory] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [exportKeyword, setExportKeyword] = useState('');
    const [exportWorkType, setExportWorkType] = useState('');
    const [exportStatus, setExportStatus] = useState('');
    const [exportEvaluated, setExportEvaluated] = useState('');
    const [exportFromDate, setExportFromDate] = useState(getDefaultFromDate);
    const [exportToDate, setExportToDate] = useState(getDefaultToDate);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        const loadAccounts = async () => {
            const response = await Get(ACCOUNTAUTHINFO_API.GET_PAGING, null, {
                pageIndex: 1,
                pageSize: 10000
            });
            setAccounts(getPagedData(response));
        };
        loadAccounts();
    }, []);

    const accountMap = useMemo(() => {
        const map = new Map();
        accounts.forEach(account => {
            if (account?.accountId) map.set(account.accountId.toLowerCase(), account);
        });
        return map;
    }, [accounts]);

    const matchingUserIds = useMemo(() => {
        const key = normalize(keyword);
        if (!key) return [];
        return accounts
            .filter(account => normalize(`${getFullName(account)} ${account?.account?.username || ''}`).includes(key))
            .map(account => account.accountId)
            .filter(Boolean);
    }, [accounts, keyword]);

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const filter = { pageIndex: page, pageSize, fromDate, toDate };
            if (evaluated !== '') filter.isEvaluated = evaluated === 'true';
            if (keyword) {
                filter.keyword = keyword;
                filter.userIds = matchingUserIds;
            }
            if (workType !== '') filter.workType = Number(workType);
            if (status !== '') filter.status = Number(status);

            const response = await Get(WORK_RESULT_API.GET_ALL_PAGING, null, filter);
            if (!response) {
                setRows([]);
                setTotal(0);
                toast.error('Không thể tải danh sách kết quả công việc.');
                return;
            }
            setRows(getPagedData(response));
            setTotal(response.totalRecords ?? response.TotalRecords ?? 0);
        } finally {
            setIsLoading(false);
        }
    }, [page, pageSize, keyword, matchingUserIds, workType, status, evaluated, fromDate, toDate]);

    useEffect(() => { load(); }, [load]);

    const displayEmployeeName = row => {
        const account = accountMap.get((row.userId || '').toLowerCase());
        return getFullName(account) || row.employeeName || '-';
    };

    const submitSearch = event => {
        event.preventDefault();
        if (fromDateInput && toDateInput && fromDateInput > toDateInput) {
            toast.warning('Từ ngày không được lớn hơn đến ngày.');
            return;
        }
        setPage(1);
        setKeyword(keywordInput.trim());
        setFromDate(fromDateInput);
        setToDate(toDateInput);
    };

    const resetFilters = () => {
        setKeywordInput('');
        setKeyword('');
        setWorkType('');
        setStatus('');
        setEvaluated('false');
        const defaultFromDate = getDefaultFromDate();
        const defaultToDate = getDefaultToDate();
        setFromDateInput(defaultFromDate);
        setToDateInput(defaultToDate);
        setFromDate(defaultFromDate);
        setToDate(defaultToDate);
        setPage(1);
    };

    const exportExcel = async event => {
        event.preventDefault();
        if (exportFromDate && exportToDate && exportFromDate > exportToDate) {
            toast.warning('Từ ngày không được lớn hơn đến ngày.');
            return;
        }

        const normalizedKeyword = exportKeyword.trim();
        const exportUserIds = normalizedKeyword
            ? accounts
                .filter(account => normalize(`${getFullName(account)} ${account?.account?.username || ''}`)
                    .includes(normalize(normalizedKeyword)))
                .map(account => account.accountId)
                .filter(Boolean)
            : [];

        const filter = { fromDate: exportFromDate, toDate: exportToDate };
        if (normalizedKeyword) {
            filter.keyword = normalizedKeyword;
            filter.userIds = exportUserIds;
        }
        if (exportWorkType !== '') filter.workType = Number(exportWorkType);
        if (exportStatus !== '') filter.status = Number(exportStatus);
        if (exportEvaluated !== '') filter.isEvaluated = exportEvaluated === 'true';

        setIsExporting(true);
        try {
            const response = await Get(WORK_RESULT_API.GET_EXPORT_DATA, null, filter);
            const data = getArrayData(response);
            if (data.length === 0) {
                toast.info('Không có dữ liệu phù hợp để xuất.');
                return;
            }

            const excelRows = data.map((row, index) => ({
                'STT': index + 1,
                'Loại': row.workType === 1 ? 'Thường niên' : 'Dự án',
                'Công việc': row.workName || '',
                'Thành viên': displayEmployeeName(row),
                'Trạng thái': row.status === 3 ? 'Hoàn thành' : 'Quá hạn',
                'Hạn hoàn thành': fmtDate(row.endDate),
                'Điểm trước đánh giá': row.baseScore ?? '',
                'Tỷ lệ': row.evaluationPercent == null ? 'Chưa đánh giá' : `${row.evaluationPercent}%`,
                'Điểm thực nhận': row.actualScore ?? '',
                'Đánh giá gần nhất': row.evaluatedBy
                    ? `${row.evaluatedBy}${row.evaluatedAt ? ` - ${fmtDate(row.evaluatedAt)}` : ''}`
                    : '-'
            }));

            const XLSX = await import('xlsx');
            const worksheet = XLSX.utils.json_to_sheet(excelRows);
            worksheet['!cols'] = [
                { wch: 7 }, { wch: 16 }, { wch: 36 }, { wch: 28 }, { wch: 16 },
                { wch: 21 }, { wch: 22 }, { wch: 18 }, { wch: 19 }, { wch: 32 }
            ];
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Kết quả công việc');

            const now = new Date();
            const pad = value => String(value).padStart(2, '0');
            const fileName = `Ket_qua_cong_viec_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}.xlsx`;
            XLSX.writeFile(workbook, fileName);
            toast.success(`Đã xuất ${data.length} công việc.`);
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setIsExporting(false);
        }
    };

    const openEvaluation = row => {
        setSelected(row);
        setPercent(row.evaluationPercent ?? '');
    };

    const closeEvaluation = () => {
        setSelected(null);
        setPercent('');
    };

    const save = async () => {
        const numericPercent = Number(percent);
        if (!selected || !Number.isInteger(numericPercent) || numericPercent <= 0) {
            toast.warning('Tỷ lệ đánh giá phải là số nguyên lớn hơn 0%.');
            return;
        }

        setIsSaving(true);
        try {
            await Put(WORK_RESULT_API.SAVE_EVALUATION, null, {
                workType: selected.workType,
                workId: selected.workId,
                userId: selected.userId,
                evaluationPercent: numericPercent
            });
            closeEvaluation();
            toast.success('Đã lưu đánh giá.');
            await load();
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const openHistory = async row => {
        setHistoryItem(row);
        setHistory([]);
        setIsHistoryLoading(true);
        try {
            const data = await Get(WORK_RESULT_API.GET_HISTORY, null, {
                workType: row.workType,
                workId: row.workId,
                userId: row.userId
            });
            setHistory(Array.isArray(data) ? data : (data?.$values || []));
        } finally {
            setIsHistoryLoading(false);
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">Kết quả công việc</h3>
                        </div>
                        <div className="card-body">
                            <Tabs defaultActiveKey="evaluation" id="work-result-tabs" className="mb-3">
                                <Tab eventKey="evaluation" title="Đánh giá kết quả">
                            <Form onSubmit={submitSearch} className="mb-3">
                                <div className="d-flex flex-wrap align-items-end">
                                    <Form.Group className="mr-2 mb-2" style={{ flex: '0 0 165px' }}>
                                        <Form.Label>Từ ngày</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={fromDateInput}
                                            onChange={event => setFromDateInput(event.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mr-2 mb-2" style={{ flex: '0 0 165px' }}>
                                        <Form.Label>Đến ngày</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={toDateInput}
                                            onChange={event => setToDateInput(event.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mr-2 mb-2" style={{ flex: '0 0 210px' }}>
                                        <Form.Label>Tìm kiếm</Form.Label>
                                        <Form.Control
                                            value={keywordInput}
                                            placeholder="Tên công việc hoặc thành viên"
                                            onChange={event => setKeywordInput(event.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mr-2 mb-2" style={{ flex: '0 0 150px' }}>
                                        <Form.Label>Loại công việc</Form.Label>
                                        <Form.Control as="select" value={workType} onChange={event => { setWorkType(event.target.value); setPage(1); }}>
                                            <option value="">Tất cả</option>
                                            <option value="1">Thường niên</option>
                                            <option value="2">Dự án / đột xuất</option>
                                        </Form.Control>
                                    </Form.Group>
                                    <Form.Group className="mr-2 mb-2" style={{ flex: '0 0 125px' }}>
                                        <Form.Label>Trạng thái</Form.Label>
                                        <Form.Control as="select" value={status} onChange={event => { setStatus(event.target.value); setPage(1); }}>
                                            <option value="">Tất cả</option>
                                            <option value="3">Hoàn thành</option>
                                            <option value="4">Quá hạn</option>
                                        </Form.Control>
                                    </Form.Group>
                                    <Form.Group className="mr-2 mb-2" style={{ flex: '0 0 140px' }}>
                                        <Form.Label>Đánh giá</Form.Label>
                                        <Form.Control as="select" value={evaluated} onChange={event => { setEvaluated(event.target.value); setPage(1); }}>
                                            <option value="false">Chưa đánh giá</option>
                                            <option value="true">Đã đánh giá</option>
                                            <option value="">Tất cả</option>
                                        </Form.Control>
                                    </Form.Group>
                                    <div className="mb-2 text-nowrap" style={{ flex: '0 0 205px' }}>
                                        <Button type="submit" variant="info" className="mr-2">
                                            <i className="fas fa-search mr-1" /> Tìm kiếm
                                        </Button>
                                        <Button type="button" variant="secondary" onClick={resetFilters}>
                                            <i className="fas fa-undo mr-1" /> Đặt lại
                                        </Button>
                                    </div>
                                </div>
                            </Form>

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="form-inline">
                                    <label className="mr-2">Hiển thị:</label>
                                    <Form.Control
                                        as="select"
                                        value={pageSize}
                                        onChange={event => { setPageSize(Number(event.target.value)); setPage(1); }}
                                    >
                                        {objectShowNumber.map(item => <option key={item.value} value={item.value}>{item.text}</option>)}
                                    </Form.Control>
                                </div>
                                <span className="text-muted">Tổng số: <strong>{total}</strong> công việc</span>
                            </div>

                            <div className="dataTables_wrapper dt-bootstrap4">
                                <Table striped bordered hover responsive style={{ fontSize: '14px' }}>
                                    <thead className="bg-light">
                                        <tr className="text-center">
                                            <th style={{ width: 55 }}>STT</th>
                                            <th>Loại</th>
                                            <th>Công việc</th>
                                            <th>Thành viên</th>
                                            <th>Trạng thái</th>
                                            <th>Hạn hoàn thành</th>
                                            <th>Điểm trước đánh giá</th>
                                            <th>Tỷ lệ</th>
                                            <th>Điểm thực nhận</th>
                                            <th>Đánh giá gần nhất</th>
                                            <th>Thao tác</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!isLoading && rows.map((row, index) => (
                                            <tr key={`${row.workType}-${row.workId}-${row.userId}`}>
                                                <td className="text-center">{(page - 1) * pageSize + index + 1}</td>
                                                <td className="text-center text-nowrap">
                                                    <Badge variant={row.workType === 1 ? 'primary' : 'info'}>
                                                        {row.workType === 1 ? 'Thường niên' : 'Dự án'}
                                                    </Badge>
                                                </td>
                                                <td><strong>{row.workName}</strong></td>
                                                <td>{displayEmployeeName(row)}</td>
                                                <td className="text-center">
                                                    <Badge variant={row.status === 3 ? 'success' : 'danger'}>
                                                        {row.status === 3 ? 'Hoàn thành' : 'Quá hạn'}
                                                    </Badge>
                                                </td>
                                                <td className="text-center text-nowrap">{fmtDate(row.endDate)}</td>
                                                <td className="text-right">{fmt(row.baseScore)}</td>
                                                <td className="text-center">
                                                    {row.evaluationPercent == null
                                                        ? <Badge variant="secondary">Chưa đánh giá</Badge>
                                                        : <strong className="text-primary">{row.evaluationPercent}%</strong>}
                                                </td>
                                                <td className="text-right font-weight-bold text-success">{fmt(row.actualScore)}</td>
                                                <td className="text-nowrap">
                                                    {row.evaluatedBy || '-'}
                                                    {row.evaluatedAt && <><br /><small className="text-muted">{fmtDate(row.evaluatedAt)}</small></>}
                                                </td>
                                                <td className="text-center text-nowrap">
                                                    <Button size="sm" variant="info" className="mr-1" onClick={() => openEvaluation(row)}>
                                                        <i className="fas fa-star mr-1" /> Đánh giá
                                                    </Button>
                                                    <Button size="sm" variant="outline-secondary" onClick={() => openHistory(row)}>
                                                        <i className="fas fa-history mr-1" /> Lịch sử
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        {!isLoading && rows.length === 0 && (
                                            <tr><td colSpan="11" className="text-center text-muted py-4">Không có công việc phù hợp.</td></tr>
                                        )}
                                        {isLoading && (
                                            <tr><td colSpan="11" className="text-center py-5">
                                                <span className="spinner-border text-info" role="status" aria-hidden="true" />
                                                <div className="text-muted mt-2">Đang tải dữ liệu...</div>
                                            </td></tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {total > pageSize && (
                                <div className="d-flex justify-content-center">
                                    <Pagination
                                        activePage={page}
                                        itemsCountPerPage={pageSize}
                                        totalItemsCount={total}
                                        pageRangeDisplayed={5}
                                        onChange={setPage}
                                        itemClass="page-item"
                                        linkClass="page-link"
                                    />
                                </div>
                            )}
                                </Tab>
                                <Tab eventKey="export" title="Xuất dữ liệu">
                                    <div className="alert alert-light border mb-3">
                                        Chọn điều kiện xuất. File Excel sẽ chứa toàn bộ dữ liệu phù hợp, không giới hạn theo trang đang xem.
                                    </div>
                                    <Form onSubmit={exportExcel}>
                                        <div className="d-flex flex-wrap align-items-end">
                                            <Form.Group className="mr-2 mb-3" style={{ flex: '0 0 165px' }}>
                                                <Form.Label>Từ ngày</Form.Label>
                                                <Form.Control type="date" value={exportFromDate} onChange={event => setExportFromDate(event.target.value)} />
                                            </Form.Group>
                                            <Form.Group className="mr-2 mb-3" style={{ flex: '0 0 165px' }}>
                                                <Form.Label>Đến ngày</Form.Label>
                                                <Form.Control type="date" value={exportToDate} onChange={event => setExportToDate(event.target.value)} />
                                            </Form.Group>
                                            <Form.Group className="mr-2 mb-3" style={{ flex: '0 0 210px' }}>
                                                <Form.Label>Tìm kiếm</Form.Label>
                                                <Form.Control
                                                    value={exportKeyword}
                                                    placeholder="Tên công việc hoặc thành viên"
                                                    onChange={event => setExportKeyword(event.target.value)}
                                                />
                                            </Form.Group>
                                            <Form.Group className="mr-2 mb-3" style={{ flex: '0 0 150px' }}>
                                                <Form.Label>Loại công việc</Form.Label>
                                                <Form.Control as="select" value={exportWorkType} onChange={event => setExportWorkType(event.target.value)}>
                                                    <option value="">Tất cả</option>
                                                    <option value="1">Thường niên</option>
                                                    <option value="2">Dự án / đột xuất</option>
                                                </Form.Control>
                                            </Form.Group>
                                            <Form.Group className="mr-2 mb-3" style={{ flex: '0 0 125px' }}>
                                                <Form.Label>Trạng thái</Form.Label>
                                                <Form.Control as="select" value={exportStatus} onChange={event => setExportStatus(event.target.value)}>
                                                    <option value="">Tất cả</option>
                                                    <option value="3">Hoàn thành</option>
                                                    <option value="4">Quá hạn</option>
                                                </Form.Control>
                                            </Form.Group>
                                            <Form.Group className="mr-2 mb-3" style={{ flex: '0 0 140px' }}>
                                                <Form.Label>Đánh giá</Form.Label>
                                                <Form.Control as="select" value={exportEvaluated} onChange={event => setExportEvaluated(event.target.value)}>
                                                    <option value="">Tất cả</option>
                                                    <option value="true">Đã đánh giá</option>
                                                    <option value="false">Chưa đánh giá</option>
                                                </Form.Control>
                                            </Form.Group>
                                            <div className="mb-3 text-nowrap" style={{ flex: '0 0 150px' }}>
                                                <Button type="submit" variant="success" disabled={isExporting}>
                                                    <i className="fas fa-file-excel mr-1" />
                                                    {isExporting ? 'Đang xuất dữ liệu...' : 'Xuất Excel'}
                                                </Button>
                                            </div>
                                        </div>
                                    </Form>
                                </Tab>
                            </Tabs>
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={!!selected} onHide={closeEvaluation} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Đánh giá công việc</Modal.Title>
                </Modal.Header>
                {selected && (
                    <Modal.Body>
                        <div className="p-3 mb-3 bg-light rounded">
                            <div className="font-weight-bold text-primary mb-1">{selected.workName}</div>
                            <div>Thành viên: <strong>{displayEmployeeName(selected)}</strong></div>
                            <div>Điểm trước đánh giá: <strong>{fmt(selected.baseScore)}</strong></div>
                        </div>
                        <Form.Group>
                            <Form.Label>Tỷ lệ đánh giá (%) <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="number"
                                min="1"
                                step="1"
                                value={percent}
                                placeholder="Nhập số nguyên lớn hơn 0"
                                onChange={event => setPercent(event.target.value)}
                            />
                            <Form.Text className="text-muted">Có thể nhập lớn hơn 100%.</Form.Text>
                        </Form.Group>
                        <div className="alert alert-info mb-0">
                            Điểm thực nhận: <strong>{Number(percent) > 0 && Number.isInteger(Number(percent))
                                ? fmt(selected.baseScore * Number(percent) / 100)
                                : '-'}</strong>
                        </div>
                    </Modal.Body>
                )}
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeEvaluation} disabled={isSaving}>Đóng</Button>
                    <Button variant="success" onClick={save} disabled={isSaving}>
                        {isSaving ? 'Đang lưu...' : 'Lưu đánh giá'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal size="lg" show={!!historyItem} onHide={() => setHistoryItem(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Lịch sử đánh giá</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {historyItem && <div className="mb-3"><strong>{historyItem.workName}</strong> — {displayEmployeeName(historyItem)}</div>}
                    <Table striped bordered hover responsive size="sm">
                        <thead className="bg-light">
                            <tr className="text-center">
                                <th>Thời gian</th>
                                <th>Người chấm</th>
                                <th>Điểm gốc</th>
                                <th>Tỷ lệ cũ</th>
                                <th>Tỷ lệ mới</th>
                                <th>Điểm mới</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!isHistoryLoading && history.map(item => (
                                <tr key={item.id}>
                                    <td className="text-nowrap">{fmtDate(item.evaluatedAt)}</td>
                                    <td>{item.evaluatedBy}</td>
                                    <td className="text-right">{fmt(item.baseScore)}</td>
                                    <td className="text-center">{item.oldPercent == null ? '-' : `${item.oldPercent}%`}</td>
                                    <td className="text-center font-weight-bold text-primary">{item.newPercent}%</td>
                                    <td className="text-right font-weight-bold text-success">{fmt(item.newActualScore)}</td>
                                </tr>
                            ))}
                            {!isHistoryLoading && history.length === 0 && (
                                <tr><td colSpan="6" className="text-center text-muted py-3">Chưa có lịch sử đánh giá.</td></tr>
                            )}
                            {isHistoryLoading && (
                                <tr><td colSpan="6" className="text-center py-4">
                                    <span className="spinner-border spinner-border-sm text-info" role="status" aria-hidden="true" />
                                    <span className="ml-2">Đang tải...</span>
                                </td></tr>
                            )}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={() => setHistoryItem(null)}>Đóng</Button></Modal.Footer>
            </Modal>
        </div>
    );
};

export default WorkResultList;
